import { useEffect, useState } from "react";
import { Lock, LockOpen, Printer, FileDown, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { renderPages, printPages } from "../../lib/render";
import { toPdf, download, safeName } from "../../lib/exporters";
import { PAGE_SIZES } from "../../lib/pages";

export const ProtectDialog = ({ open, onClose, hasPassword, currentPw, onApply }) => {
  const [mode, setMode] = useState("change");
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setCur(""); setPw(""); setPw2(""); setErr(""); setMode("change"); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (hasPassword && cur !== currentPw) return setErr("Current password is incorrect");
    if (mode === "remove" && hasPassword) { await onApply(null); toast.success("Password removed — document stored unencrypted"); return onClose(); }
    if (pw.length < 4) return setErr("Use at least 4 characters");
    if (pw !== pw2) return setErr("Passwords do not match");
    await onApply(pw);
    toast.success(hasPassword ? "Password changed" : "Document encrypted with your password");
    onClose();
  };
  const newFields = (
    <>
      <div className="space-y-1"><Label>{hasPassword ? "New password" : "Password"}</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="protect-new-password" /></div>
      <div className="space-y-1"><Label>Confirm password</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="protect-confirm-password" /></div>
    </>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="protect-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="h-10 w-10 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center mb-2">{hasPassword ? <Lock className="h-5 w-5" /> : <LockOpen className="h-5 w-5" />}</div>
          <DialogTitle>{hasPassword ? "Manage password" : "Protect with password"}</DialogTitle>
          <DialogDescription>Content is encrypted in this browser with AES-256. A forgotten password cannot be recovered.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {hasPassword ? (
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 w-full"><TabsTrigger value="change" data-testid="protect-tab-change">Change</TabsTrigger><TabsTrigger value="remove" data-testid="protect-tab-remove">Remove</TabsTrigger></TabsList>
              <div className="space-y-1 mt-3"><Label>Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="protect-current-password" /></div>
              <TabsContent value="change" className="space-y-3">{newFields}</TabsContent>
              <TabsContent value="remove"><p className="text-sm text-muted-foreground">The document will be saved without encryption.</p></TabsContent>
            </Tabs>
          ) : newFields}
          {err && <p className="text-sm text-destructive" data-testid="protect-error">{err}</p>}
          <DialogFooter><Button type="submit" data-testid="protect-submit-btn">{hasPassword ? (mode === "remove" ? "Remove password" : "Change password") : "Encrypt & save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PrintPreview = ({ open, onClose, getBody, title }) => {
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [zoom, setZoom] = useState(0.8);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const { html, settings: s } = getBody();
    setSettings(s);
    setTimeout(() => setPages(renderPages(html, s)), 30);
  }, [open, getBody]);
  const pdf = async () => {
    setBusy(true);
    const { html, settings: s } = getBody();
    try { download(await toPdf(html, s), `${safeName(title)}.pdf`); } catch { toast.error("PDF export failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] w-[1100px] h-[92vh] p-0 gap-0 flex flex-col" data-testid="print-preview-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="px-5 py-3 border-b flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="text-base">Print preview</DialogTitle>
            <DialogDescription className="text-xs" data-testid="preview-page-count">{pages.length} page{pages.length !== 1 ? "s" : ""}{settings ? ` · ${PAGE_SIZES[settings.size].label.split("·")[0]}· ${settings.orientation}` : ""}</DialogDescription>
          </div>
          <div className="flex items-center gap-2 pr-8">
            <Button variant="ghost" size="icon" onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))} data-testid="preview-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
            <span className="text-xs tabular-nums w-10 text-center">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="icon" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))} data-testid="preview-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={pdf} disabled={busy} data-testid="preview-pdf-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}PDF</Button>
            <Button size="sm" onClick={() => printPages(pages, settings)} disabled={!pages.length} data-testid="preview-print-btn"><Printer className="h-4 w-4 mr-1.5" />Print</Button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto bg-muted/60 py-8" data-testid="preview-pages">
          <div className="flex flex-col items-center gap-6" style={{ zoom }}>
            {pages.map((p, i) => (
              <div key={i} className="relative" data-testid={`preview-page-${i + 1}`}>
                <div dangerouslySetInnerHTML={{ __html: p }} />
                <div className="absolute -left-12 top-0 text-xs text-muted-foreground tabular-nums">{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="px-5 py-2 border-t text-[11px] text-muted-foreground sm:justify-start">For exact output, set your printer to “Actual size / 100%” and margins to “None” or “Default”.</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
